import { useEffect, useState } from 'react'
import {
  Alert, AlertTitle, Box, Card, CardContent, Chip, CircularProgress,
  Divider, LinearProgress, Stack, Typography,
} from '@mui/material'
import { tenantApi } from '../services/api'

/**
 * The licence this install is running under.
 *
 * Exists because of an inversion rather than a feature request. On the hosted
 * product, ARIAS learns a customer is running out because the product tells
 * it — an upgrade request writes a row and emails sales. A self-hosted install
 * never calls home, which is a guarantee in the install guide, so the product
 * has to tell the customer and the customer tells ARIAS.
 *
 * Nothing did the telling. The licence has carried an expiry, a grace period
 * and warnings since it was built, with no surface rendering any of it. A
 * ceiling nobody can see produces no conversation, which is indistinguishable
 * from having no mechanism at all.
 *
 * **Renders nothing when there is no licence.** Every hosted tenant is in that
 * state, and an empty "Licence" card on the hosted product would be a worse
 * bug than the gap this closes.
 */

interface CeilingReport {
  known: boolean
  ceiling?: number
  observed?: number
  over?: boolean
  detail: string
}

interface LicenceStatus {
  licensed: boolean
  status?: string
  usable?: boolean
  detail: string
  days_remaining?: number | null
  warnings?: string[]
  ceiling?: CeilingReport
  tenants?: number
  customer_name?: string
  deployment_name?: string
  topology?: string
  expires_at?: string
  features?: string[]
}

const LicenceStatusPanel = () => {
  const [data, setData] = useState<LicenceStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    tenantApi
      .getLicenceStatus()
      .then((resp) => setData(resp.data))
      // Silent on failure, deliberately. This panel is additional context, not
      // a control: a tenant admin without platform rights gets a 403, and an
      // error card telling them something is wrong when nothing is would be
      // noise on every hosted install.
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <CircularProgress size={20} />
  if (!data || !data.licensed) return null

  const ceiling = data.ceiling
  const over = ceiling?.known && ceiling.over
  const used =
    ceiling?.known && ceiling.ceiling
      ? Math.min(100, ((ceiling.observed ?? 0) / ceiling.ceiling) * 100)
      : 0

  // Expired and "could not determine" are different problems and must not
  // render alike: one is a renewal, the other is an install fault.
  const severity =
    data.status === 'expired' || data.status === 'not_for_this_deployment'
      ? 'error'
      : data.status === 'grace' || over
      ? 'warning'
      : data.status === 'undetermined'
      ? 'info'
      : 'success'

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Licence</Typography>
          <Chip size="small" label={data.status} color={severity as any} />
          {data.topology && (
            <Chip size="small" variant="outlined" label={data.topology} />
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {data.customer_name}
          {data.deployment_name ? ` — ${data.deployment_name}` : ''}
        </Typography>

        {(data.status !== 'valid' || (data.warnings?.length ?? 0) > 0) && (
          <Alert severity={severity as any} sx={{ mt: 2 }}>
            <AlertTitle>{data.detail}</AlertTitle>
            {data.warnings?.map((w) => (
              <Typography key={w} variant="body2">
                {w}
              </Typography>
            ))}
            {/* The action is always "talk to us", never a payment link: these
                installs are bought by contract and invoiced. */}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Contact your ARIAS account manager to renew or extend.
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {ceiling?.known && (
          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">
                Agents governed across {data.tenants ?? 0} tenant
                {data.tenants === 1 ? '' : 's'}
              </Typography>
              <Typography variant="body2">
                <strong>{ceiling.observed}</strong> of {ceiling.ceiling}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={used}
              color={over ? 'warning' : 'primary'}
              sx={{ mt: 1, height: 8, borderRadius: 1 }}
            />
            {over && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {/* Reported, never enforced. Nothing here stops a scan: a
                    control that fires during an incident makes ARIAS the
                    problem. */}
                Over the licensed ceiling. Scanning continues — this is a
                commercial conversation, not a limit. Contact your ARIAS
                account manager.
              </Alert>
            )}
          </Box>
        )}

        {data.expires_at && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 2 }}>
            Expires {new Date(data.expires_at).toLocaleDateString()}
            {typeof data.days_remaining === 'number'
              ? ` — ${data.days_remaining} day${data.days_remaining === 1 ? '' : 's'} remaining`
              : ''}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default LicenceStatusPanel
